import React from "react";
import Seo from "@/components/Seo";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { SpecialOffer } from "@/components/SpecialOffer";
import { TestDriveForm } from "@/components/forms/TestDriveForm";
import { CallbackMini } from "@/components/forms/CallbackMini";
import { ClipboardList, PhoneCall, Wallet, ShieldCheck } from "lucide-react";

const STEPS = [
  { n: "1", icon: ClipboardList, title: "Prenota online", text: "Compili il modulo con i tuoi dati. È gratis, senza impegno e senza alcun pagamento." },
  { n: "2", icon: PhoneCall, title: "Ti contattiamo", text: "Un consulente ti richiama per confermare disponibilità, tempi di consegna ed eventuali agevolazioni." },
  { n: "3", icon: Wallet, title: "Perfezioni l'ordine", text: "Completi l'acquisto con l'azienda tramite i metodi di pagamento tradizionali (es. bonifico)." },
];

export default function OffertaSpeciale() {
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Offer",
    name: "Offerta speciale SENIORCAR — conferma ordine online",
    price: "5490", priceCurrency: "EUR",
    description: "SENIORCAR a €5.490 + IVA + trasporto solo per conferma ordine online, consegna in 3 mesi. Consegna rapida (8 giorni) momentaneamente esaurita.",
    availability: "https://schema.org/InStock",
    itemOffered: { "@type": "Product", name: "SENIORCAR — dispositivo medico monoposto per la mobilità" },
  };
  return (
    <>
      <Seo title="Offerta speciale SENIORCAR — €5.490 + IVA"
        description="Offerta speciale solo per conferma ordine online: SENIORCAR a €5.490 + IVA + trasporto con consegna in 3 mesi. Nessun pagamento online."
        keywords="offerta seniorcar, prezzo seniorcar, prenota seniorcar" jsonLd={jsonLd} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6"><Breadcrumbs items={[{ label: "Offerta speciale" }]} /></div>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-4">
        <img src="https://customer-assets-rejwkqb3.emergentagent.net/job_seniorcar-discover/artifacts/4c34b0723c9344f2_A1C996A0-54BA-42D1-A436-A14BF8CA922D.png"
          alt="SENIORCAR: dispositivo medico monoposto con abitacolo chiuso"
          loading="lazy" className="w-full h-[300px] sm:h-[440px] object-cover object-center rounded-t-3xl border border-slate-200 border-b-0 shadow-md" data-testid="offer-car-photo" />
      </section>

      {/* Blocco offerta attaccato all'immagine per un unico blocco visivo */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 [&_[data-testid=special-offer]>div]:rounded-t-none">
        <SpecialOffer compact formMode />
      </div>

      {/* Funnel: come funziona */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-14" data-testid="offer-howitworks">
        <div className="text-center max-w-2xl mx-auto">
          <span className="uppercase text-xs font-bold tracking-widest text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full">Come funziona</span>
          <h2 className="mt-4 text-2xl sm:text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit" }}>Prenoti ora, paghi dopo con l'azienda</h2>
          <p className="mt-3 text-slate-600 text-lg">Online non paghi nulla. La prenotazione serve solo a bloccare l'offerta: sarà l'azienda a ricontattarti per finalizzare l'ordine con i metodi di pagamento tradizionali.</p>
        </div>
        <div className="mt-10 grid sm:grid-cols-3 gap-5 relative">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="relative rounded-2xl bg-white border border-slate-200 p-6 shadow-sm" data-testid={`offer-step-${s.n}`}>
                <span className="absolute -top-4 left-6 w-9 h-9 rounded-full flex items-center justify-center font-extrabold text-white shadow" style={{ backgroundColor: "var(--sc-emerald)" }}>{s.n}</span>
                <Icon size={30} className="text-[var(--sc-emerald)] mt-2" />
                <h3 className="mt-3 text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{s.title}</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{s.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Form finale */}
      <section id="offer-form" className="max-w-3xl mx-auto px-4 sm:px-6 mt-14 scroll-mt-24" data-testid="offer-form">
        <div className="text-center max-w-xl mx-auto mb-6">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit" }}>Prenota la tua SENIORCAR</h2>
          <p className="mt-2 text-slate-600">Ultimo passaggio: lasciaci i tuoi dati. Non è necessario alcun pagamento online — ti contatteremo per perfezionare l'ordine.</p>
        </div>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 mb-6 flex items-start gap-3" data-testid="offer-form-reassurance">
          <ShieldCheck size={22} className="mt-0.5 shrink-0 text-emerald-700" />
          <p className="text-sm text-emerald-900 leading-relaxed">
            <strong>Nessun pagamento ora.</strong> Inserendo i tuoi dati prenoti l'offerta senza impegno: l'azienda ti ricontatterà per completare l'ordine con i metodi di pagamento tradizionali.
          </p>
        </div>
        <TestDriveForm />
        <div className="mt-6"><CallbackMini source="offer-form" /></div>
      </section>
      <div className="h-16" />
    </>
  );
}
